import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowLeft, FaPlus, FaTrash, FaSpinner, FaSave, FaEdit, FaTimes,
  FaImage, FaVolumeUp, FaCheckCircle, FaMicrophone,
} from "react-icons/fa";

type Module = "LESEN" | "HOREN" | "SCHREIBEN" | "SPRECHEN";
type QuestionType =
  | "TRUE_FALSE" | "MULTIPLE_CHOICE" | "MULTIPLE_CHOICE_4"
  | "MATCHING" | "TEXT_INPUT" | "FILL_BLANKS"
  | "ESSAY" | "EMAIL" | "LETTER" | "OTHER";

interface Question {
  id?: number;
  module: Module;
  questionNumber: number;
  questionType: QuestionType;
  questionText: string;
  instructions: string;
  contextText: string;
  contextImageUrl?: string;
  contextAudioUrl?: string;
  options: string;
  correctAnswer: string;
  blankCount?: number;
  points: number;
  isActive: boolean;
  autoGradable?: boolean;
}

interface ClassTest {
  id?: number;
  title: string;
  level: string;
  chapterRange: string;
  lesenDuration: number | "";
  horenDuration: number | "";
  schreibenDuration: number | "";
  sprechenDuration: number | "";
  horenAudioUrl?: string;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MODULES: Module[] = ["LESEN", "HOREN", "SCHREIBEN", "SPRECHEN"];
const MODULE_LABELS: Record<Module, string> = {
  LESEN: "Lesen",
  HOREN: "Hören",
  SCHREIBEN: "Schreiben",
  SPRECHEN: "Sprechen",
};
const MODULE_COLORS: Record<Module, string> = {
  LESEN: "bg-blue-100 text-blue-800 border-blue-200",
  HOREN: "bg-purple-100 text-purple-800 border-purple-200",
  SCHREIBEN: "bg-green-100 text-green-800 border-green-200",
  SPRECHEN: "bg-orange-100 text-orange-800 border-orange-200",
};
const LEVELS = ["A1", "A2", "B1", "B2"];
const CHAPTER_RANGES = [
  { value: "CHAPTERS_1_3", label: "Chapters 1–3" },
  { value: "CHAPTERS_4_6", label: "Chapters 4–6" },
  { value: "CHAPTERS_7_9", label: "Chapters 7–9" },
  { value: "CHAPTERS_10_12", label: "Chapters 10–12" },
];

// Question types available per module
const TYPES_BY_MODULE: Record<Module, { value: QuestionType; label: string }[]> = {
  LESEN: [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice (3 options)" },
    { value: "MULTIPLE_CHOICE_4", label: "Multiple Choice (4 options)" },
    { value: "TRUE_FALSE", label: "True / False (Richtig / Falsch)" },
    { value: "FILL_BLANKS", label: "Fill in the Blank(s)" },
    { value: "MATCHING", label: "Matching (Zuordnung)" },
    { value: "TEXT_INPUT", label: "Short Text Input" },
    { value: "OTHER", label: "Other" },
  ],
  HOREN: [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice (3 options)" },
    { value: "MULTIPLE_CHOICE_4", label: "Multiple Choice (4 options)" },
    { value: "TRUE_FALSE", label: "True / False (Richtig / Falsch)" },
    { value: "FILL_BLANKS", label: "Fill in the Blank(s)" },
    { value: "MATCHING", label: "Matching (Zuordnung)" },
    { value: "TEXT_INPUT", label: "Short Text Input" },
    { value: "OTHER", label: "Other" },
  ],
  SCHREIBEN: [
    { value: "FILL_BLANKS", label: "Fill in the Blank(s)" },
    { value: "TEXT_INPUT", label: "Short Text Input" },
    { value: "ESSAY", label: "Essay" },
    { value: "EMAIL", label: "Email Writing" },
    { value: "LETTER", label: "Letter Writing" },
    { value: "OTHER", label: "Other" },
  ],
  SPRECHEN: [], // no type selector
};

const BLANK_QUESTION = (module: Module): Partial<Question> => ({
  module,
  questionType: module === "SPRECHEN" ? "OTHER"
    : module === "SCHREIBEN" ? "ESSAY"
    : "MULTIPLE_CHOICE",
  questionText: "",
  instructions: "",
  contextText: "",
  options: JSON.stringify(["", "", ""]),
  correctAnswer: "",
  points: 1,
  isActive: true,
});

interface Props {
  classTestId: number | null;
  onBack: () => void;
}

const AdminClassTestEditor: React.FC<Props> = ({ classTestId, onBack }) => {
  const [test, setTest] = useState<ClassTest>({
    title: "",
    level: "A1",
    chapterRange: "CHAPTERS_1_3",
    lesenDuration: "",
    horenDuration: "",
    schreibenDuration: "",
    sprechenDuration: "",
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [activeModule, setActiveModule] = useState<Module>("LESEN");
  const [loading, setLoading] = useState(!!classTestId);
  const [saving, setSaving] = useState(false);
  const [editingQ, setEditingQ] = useState<Partial<Question> | null>(null);
  const [editingQId, setEditingQId] = useState<number | "new" | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(!!classTestId);

  useEffect(() => {
    if (!classTestId) return;
    const load = async () => {
      const res = await fetch(`${API_BASE}/api/admin/class-tests/${classTestId}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setTest({
        id: data.id,
        title: data.title,
        level: data.level,
        chapterRange: data.chapterRange,
        lesenDuration: data.lesenDuration ?? "",
        horenDuration: data.horenDuration ?? "",
        schreibenDuration: data.schreibenDuration ?? "",
        sprechenDuration: data.sprechenDuration ?? "",
        horenAudioUrl: data.horenAudioUrl ?? undefined,
      });
      setQuestions(data.questions || []);
      setLoading(false);
    };
    load();
  }, [classTestId]);

  const handleSaveTest = async () => {
    if (!test.title || !test.level || !test.chapterRange) {
      setError("Title, level and chapter range are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        title: test.title,
        level: test.level,
        chapterRange: test.chapterRange,
        lesenDuration: test.lesenDuration || null,
        horenDuration: test.horenDuration || null,
        schreibenDuration: test.schreibenDuration || null,
        sprechenDuration: test.sprechenDuration || null,
      };
      const url = (test as any).id
        ? `${API_BASE}/api/admin/class-tests/${(test as any).id}`
        : `${API_BASE}/api/admin/class-tests`;
      const res = await fetch(url, {
        method: (test as any).id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setTest((prev) => ({ ...prev, id: data.id } as any));
      setSaved(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openNewQuestion = (module: Module) => {
    setEditingQ(BLANK_QUESTION(module));
    setEditingQId("new");
  };

  const openEditQuestion = (q: Question) => {
    setEditingQ({ ...q });
    setEditingQId(q.id!);
  };

  const handleSaveQuestion = async (): Promise<number | null> => {
    if (!editingQ || !(test as any).id) return null;
    setSaving(true);
    setError("");
    try {
      const isNew = editingQId === "new";
      const url = isNew
        ? `${API_BASE}/api/admin/class-tests/${(test as any).id}/questions`
        : `${API_BASE}/api/admin/class-tests/questions/${editingQId}`;
      const payload = { ...editingQ };
      // SPRECHEN doesn't need a type — send OTHER
      if (payload.module === "SPRECHEN" && !payload.questionType) {
        payload.questionType = "OTHER";
      }
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save question");
      const savedQ: Question = await res.json();
      if (isNew) {
        setQuestions((prev) => [...prev, savedQ]);
      } else {
        setQuestions((prev) => prev.map((q) => (q.id === savedQ.id ? savedQ : q)));
      }
      // Switch from "new" to the real ID so uploads can happen
      setEditingQId(savedQ.id!);
      setEditingQ({ ...savedQ });
      return savedQ.id!;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (qId: number) => {
    if (!window.confirm("Delete this question?")) return;
    await fetch(`${API_BASE}/api/admin/class-tests/questions/${qId}`, {
      method: "DELETE",
      credentials: "include",
    });
    setQuestions((prev) => prev.filter((q) => q.id !== qId));
  };

  const closeModal = () => {
    setEditingQ(null);
    setEditingQId(null);
    // Refresh questions from state (already updated)
  };

  const refreshQuestion = (updated: Question) => {
    setEditingQ({ ...updated });
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
  };

  const moduleQuestions = questions.filter((q) => q.module === activeModule && q.isActive);

  if (loading) return (
    <div className="flex justify-center py-20">
      <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
          <FaArrowLeft />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">
          {classTestId ? "Edit Class Test" : "New Class Test"}
        </h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Test Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-semibold text-gray-800 text-lg">Test Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={test.title}
              onChange={(e) => setTest({ ...test, title: e.target.value })}
              placeholder="e.g. A1 – Chapters 1–3 Class Test"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
            <select
              value={test.level}
              onChange={(e) => setTest({ ...test, level: e.target.value })}
              disabled={!!classTestId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] disabled:bg-gray-100"
            >
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chapter Range</label>
            <select
              value={test.chapterRange}
              onChange={(e) => setTest({ ...test, chapterRange: e.target.value })}
              disabled={!!classTestId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] disabled:bg-gray-100"
            >
              {CHAPTER_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(["Lesen", "Horen", "Schreiben", "Sprechen"] as const).map((mod) => {
            const key = `${mod.toLowerCase()}Duration` as keyof ClassTest;
            return (
              <div key={mod}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mod === "Horen" ? "Hören" : mod} (min)
                </label>
                <input
                  type="number"
                  min={1}
                  value={test[key] as number | ""}
                  onChange={(e) =>
                    setTest({ ...test, [key]: e.target.value ? Number(e.target.value) : "" })
                  }
                  placeholder="—"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                />
              </div>
            );
          })}
        </div>
        <button
          onClick={handleSaveTest}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] transition-colors disabled:opacity-50"
        >
          {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
          {saved ? "Save Changes" : "Create & Continue"}
        </button>
      </div>

      {/* Questions */}
      {saved && (test as any).id && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800 text-lg">Questions</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Sprechen questions are visible to admin/mentor only.
            </p>
          </div>

          {/* Module Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {MODULES.map((mod) => {
              const count = questions.filter((q) => q.module === mod && q.isActive).length;
              return (
                <button
                  key={mod}
                  onClick={() => setActiveModule(mod)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeModule === mod
                      ? "text-[#009fe3] border-b-2 border-[#009fe3] bg-blue-50"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {MODULE_LABELS[mod]}
                  {count > 0 && (
                    <span className="bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5 text-xs">{count}</span>
                  )}
                  {mod === "SPRECHEN" && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                      Admin only
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-6 space-y-3">
            {moduleQuestions.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-6">
                No {MODULE_LABELS[activeModule]} questions yet.
              </p>
            )}

            {moduleQuestions.map((q) => {
              const bc = q.blankCount && q.blankCount > 1 ? q.blankCount : 1;
              const startNum = q.questionNumber;
              const endNum = bc > 1 ? startNum + bc - 1 : null;
              const numLabel = endNum ? `Q${startNum}–${endNum}` : `Q${startNum}`;
              return (
              <div
                key={q.id}
                className={`rounded-lg border p-4 flex items-start gap-3 ${MODULE_COLORS[q.module]}`}
              >
                <span className="font-bold text-sm shrink-0 whitespace-nowrap">{numLabel}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {q.module !== "SPRECHEN" && (
                      <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
                        {q.questionType.replace(/_/g, " ")}
                      </span>
                    )}
                    <span className="text-xs opacity-60">· {q.points} pt{q.points !== 1 ? "s" : ""}</span>
                    {q.autoGradable && (
                      <span className="text-xs bg-white/70 px-1.5 py-0.5 rounded">Auto-grade</span>
                    )}
                    {q.contextImageUrl && (
                      <span className="text-xs flex items-center gap-0.5 opacity-70">
                        <FaImage size={10} /> Photo{bc > 1 ? ` (${bc} Qs)` : ""}
                      </span>
                    )}
                    {q.contextAudioUrl && (
                      <span className="text-xs flex items-center gap-0.5 opacity-70"><FaVolumeUp size={10} /> Audio</span>
                    )}
                  </div>
                  <p className="text-sm font-medium line-clamp-2">{q.questionText || "(no text)"}</p>
                  {q.contextText && (
                    <p className="text-xs mt-1 opacity-70 line-clamp-1">Context: {q.contextText}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => openEditQuestion(q)}
                    className="p-1.5 hover:bg-white/50 rounded transition-colors"
                    title="Edit"
                  >
                    <FaEdit size={13} />
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(q.id!)}
                    className="p-1.5 hover:bg-white/50 rounded transition-colors text-red-600"
                    title="Delete"
                  >
                    <FaTrash size={13} />
                  </button>
                </div>
              </div>
            );
            })}

            <button
              onClick={() => openNewQuestion(activeModule)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#009fe3] hover:text-[#009fe3] transition-colors w-full justify-center"
            >
              <FaPlus /> Add {MODULE_LABELS[activeModule]} Question
            </button>
          </div>
        </div>
      )}

      {/* Question Edit Modal */}
      {editingQ && (
        <QuestionModal
          question={editingQ}
          questionId={editingQId === "new" ? null : editingQId as number}
          onChange={setEditingQ}
          onSave={handleSaveQuestion}
          onClose={closeModal}
          onRefresh={refreshQuestion}
          saving={saving}
          testId={(test as any).id!}
          horenAudioUrl={test.horenAudioUrl}
          onHorenAudioUploaded={(url) => setTest((prev) => ({ ...prev, horenAudioUrl: url }))}
        />
      )}
    </div>
  );
};

// ─── Question Modal ──────────────────────────────────────────────────────────

interface ModalProps {
  question: Partial<Question>;
  questionId: number | null;
  onChange: (q: Partial<Question>) => void;
  onSave: () => Promise<number | null>;
  onClose: () => void;
  onRefresh: (q: Question) => void;
  saving: boolean;
  testId: number;
  horenAudioUrl?: string;
  onHorenAudioUploaded: (url: string) => void;
}

const QuestionModal: React.FC<ModalProps> = ({
  question, questionId, onChange, onSave, onClose, onRefresh, saving,
  testId, horenAudioUrl, onHorenAudioUploaded,
}) => {
  const set = (field: keyof Question, value: any) => onChange({ ...question, [field]: value });
  const [saveMsg, setSaveMsg] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingHorenAudio, setUploadingHorenAudio] = useState(false);
  const [pendingImg, setPendingImg] = useState<File | null>(null);
  const [pendingHorenAudio, setPendingHorenAudio] = useState<File | null>(null);
  const imgRef = useRef<HTMLInputElement>(null);
  const horenAudioRef = useRef<HTMLInputElement>(null);

  const API = process.env.REACT_APP_API_BASE_URL;

  const mod = question.module as Module;
  const types = TYPES_BY_MODULE[mod] ?? [];
  const isMultiChoice = ["MULTIPLE_CHOICE", "MULTIPLE_CHOICE_4"].includes(question.questionType || "");
  const isTrueFalse = question.questionType === "TRUE_FALSE";
  const isFillBlanks = question.questionType === "FILL_BLANKS";
  const isWriting = ["ESSAY", "EMAIL", "LETTER", "TEXT_INPUT", "OTHER"].includes(question.questionType || "");
  const isMatching = question.questionType === "MATCHING";
  const isSprechen = mod === "SPRECHEN";
  const isHoren = mod === "HOREN";
  const supportsMultiAnswer = isMultiChoice || isTrueFalse || isFillBlanks;
  // Hören photo = photo IS the question; hide text + option inputs, only show answer selectors
  const photoIsQuestion = isHoren && !!question.contextImageUrl;

  // blankCount: how many sub-questions are in this entry (for photo-based multi-Q)
  const autoBlankCount = isFillBlanks
    ? Math.max(1, (question.questionText || "").split("___").length - 1)
    : 1;
  const [blankCount, setBlankCount] = useState<number>(
    () => question.blankCount ?? autoBlankCount
  );
  const [blankCountStr, setBlankCountStr] = useState<string>(
    () => String(question.blankCount ?? autoBlankCount)
  );
  const isMultiAnswer = blankCount > 1;

  // Shared helpers for multi-answer types
  const getMultiAnswers = (): string[] => {
    try {
      const p = JSON.parse(question.correctAnswer || "[]");
      if (Array.isArray(p)) return p;
    } catch {}
    return question.correctAnswer ? [question.correctAnswer] : [];
  };
  const setMultiAnswer = (idx: number, val: string) => {
    const arr = getMultiAnswers();
    while (arr.length <= idx) arr.push("");
    arr[idx] = val;
    set("correctAnswer", JSON.stringify(arr));
  };
  const updateBlankCount = (n: number) => {
    setBlankCount(n);
    set("blankCount", n);
  };

  const getOptions = (): string[] => {
    try { return JSON.parse(question.options || "[]"); } catch { return []; }
  };
  const mcOptions: string[] = photoIsQuestion
    ? Array.from({ length: question.questionType === "MULTIPLE_CHOICE_4" ? 4 : 3 }, () => "")
    : getOptions();
  const setOption = (idx: number, val: string) => {
    const opts = getOptions(); opts[idx] = val;
    set("options", JSON.stringify(opts));
  };
  const addOption = () => set("options", JSON.stringify([...getOptions(), ""]));
  const removeOption = (idx: number) =>
    set("options", JSON.stringify(getOptions().filter((_, i) => i !== idx)));

  const handleSaveAndContinue = async () => {
    const id = await onSave();
    if (id) setSaveMsg("Question saved.");
  };

  const uploadFile = async (file: File, type: "image" | "audio") => {
    // Auto-save question first if not yet saved
    let qId = questionId;
    if (!qId) {
      setSaveMsg("Saving question first…");
      qId = await onSave();
      if (!qId) { setSaveMsg("Save failed — cannot upload."); return; }
    }
    const endpoint = `${API}/api/admin/class-tests/questions/${qId}/image`;
    setUploadingImg(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(endpoint, { method: "POST", credentials: "include", body: form });
      if (res.ok) {
        const updated: Question = await res.json();
        onRefresh(updated);
        setSaveMsg(`${type === "image" ? "Photo" : "Audio"} uploaded.`);
      } else {
        setSaveMsg("Upload failed.");
      }
    } finally {
      setUploadingImg(false);
    }
  };

  const uploadHorenSectionAudio = async (file: File) => {
    setUploadingHorenAudio(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/api/admin/class-tests/${testId}/horen-audio`, {
        method: "POST", credentials: "include", body: form,
      });
      if (res.ok) {
        const updated = await res.json();
        onHorenAudioUploaded(updated.horenAudioUrl);
        setSaveMsg("Hören audio uploaded.");
      } else {
        setSaveMsg("Upload failed.");
      }
    } finally {
      setUploadingHorenAudio(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {questionId ? "Edit Question" : "New Question"}
            </h3>
            {isSprechen && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full mt-0.5 inline-block">
                <FaMicrophone className="inline mr-1" size={10} />Sprechen — Admin/Mentor only
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <FaTimes />
          </button>
        </div>

        {/* Hören section audio — shown at top for all Hören questions */}
        {isHoren && (
          <div className="mx-5 mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-purple-800 mb-2">
              <FaVolumeUp size={13} />
              Hören Section Audio
              <span className="text-xs font-normal text-purple-600">— plays once for the entire Hören module</span>
            </div>
            {horenAudioUrl && !pendingHorenAudio && (
              <div className="flex items-center gap-3 mb-2">
                <audio controls src={horenAudioUrl} className="h-8 max-w-xs" />
              </div>
            )}
            {pendingHorenAudio ? (
              <div className="flex items-center gap-3">
                <FaVolumeUp className="text-purple-500 shrink-0" size={14} />
                <span className="text-sm text-gray-700 truncate max-w-xs">{pendingHorenAudio.name}</span>
                <button
                  onClick={() => { uploadHorenSectionAudio(pendingHorenAudio); setPendingHorenAudio(null); }}
                  disabled={uploadingHorenAudio}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 shrink-0"
                >
                  {uploadingHorenAudio ? <FaSpinner className="animate-spin" size={11} /> : null}
                  Upload
                </button>
                <button
                  onClick={() => setPendingHorenAudio(null)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => horenAudioRef.current?.click()}
                className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-purple-300 rounded-lg text-sm text-purple-700 hover:border-purple-500 transition-colors"
              >
                <FaVolumeUp size={12} />
                {horenAudioUrl ? "Replace audio" : "Attach Hören audio"}
              </button>
            )}
            <input
              ref={horenAudioRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setPendingHorenAudio(f);
                e.target.value = "";
              }}
            />
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* Module & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select
                value={question.module}
                onChange={(e) => {
                  const m = e.target.value as Module;
                  const defaultType = m === "SPRECHEN" ? "OTHER"
                    : m === "SCHREIBEN" ? "ESSAY"
                    : "MULTIPLE_CHOICE";
                  onChange({ ...question, module: m, questionType: defaultType as QuestionType });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              >
                {MODULES.map((m) => <option key={m} value={m}>{MODULE_LABELS[m]}</option>)}
              </select>
            </div>
            {!isSprechen && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                <select
                  value={question.questionType}
                  onChange={(e) => set("questionType", e.target.value as QuestionType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                >
                  {types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Points & Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
              <input
                type="number" min={1}
                value={question.points || 1}
                onChange={(e) => set("points", Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question # (optional)</label>
              <input
                type="number" min={1}
                value={question.questionNumber || ""}
                onChange={(e) => set("questionNumber", e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Auto"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions (optional)</label>
            <input
              type="text"
              value={question.instructions || ""}
              onChange={(e) => set("instructions", e.target.value)}
              placeholder="Task instructions..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            />
          </div>

          {/* Context text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Context / Passage (optional)
              <span className="ml-1 text-xs text-gray-400 font-normal">— shared by multiple questions</span>
            </label>
            <textarea
              rows={3}
              value={question.contextText || ""}
              onChange={(e) => set("contextText", e.target.value)}
              placeholder="Reading text, article, dialogue, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            />
          </div>

          {/* Question Text — hidden when photo IS the question (Hören with photo) */}
          {!photoIsQuestion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isSprechen ? "Speaking Prompt / Topic" : "Question Text"}
            </label>
            <textarea
              rows={isSprechen ? 3 : 2}
              value={question.questionText || ""}
              onChange={(e) => set("questionText", e.target.value)}
              placeholder={
                isSprechen ? "Enter speaking topic or prompt..."
                  : isFillBlanks ? "Use ___ for each blank, or leave empty if photo contains the questions."
                  : "Enter the question..."
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            />
          </div>
          )}

          {/* Multiple Choice options */}
          {isMultiChoice && (
            <div>
              {/* Option text inputs — hidden when photo contains the questions */}
              {!photoIsQuestion && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer Options</label>
                <div className="space-y-2">
                  {getOptions().map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm font-mono w-5 text-gray-500">{String.fromCharCode(97 + idx)})</span>
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => setOption(idx, e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                      />
                      {getOptions().length > 2 && (
                        <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600">
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {getOptions().length < 6 && (
                    <button onClick={addOption} className="text-sm text-[#009fe3] hover:underline">+ Add option</button>
                  )}
                </div>
              </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isMultiAnswer ? `Correct Answers (${blankCount} questions)` : "Correct Answer"}
                </label>
                {isMultiAnswer ? (
                  <div className="space-y-2">
                    {Array.from({ length: blankCount }).map((_, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 shrink-0 w-24">Q{(question.questionNumber ?? 1) + idx}</span>
                        <select
                          value={getMultiAnswers()[idx] || "a"}
                          onChange={(e) => setMultiAnswer(idx, e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                        >
                          {mcOptions.map((opt, i) => (
                            <option key={i} value={String.fromCharCode(97 + i)}>
                              {String.fromCharCode(97 + i)}{opt ? `) ${opt}` : ")"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                ) : (
                  <select
                    value={question.correctAnswer || "a"}
                    onChange={(e) => set("correctAnswer", e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                  >
                    {mcOptions.map((opt, idx) => (
                      <option key={idx} value={String.fromCharCode(97 + idx)}>
                        {String.fromCharCode(97 + idx)}{opt ? `) ${opt}` : ")"}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* True / False */}
          {isTrueFalse && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isMultiAnswer ? `Correct Answers (${blankCount} questions)` : "Correct Answer"}
              </label>
              {isMultiAnswer ? (
                <div className="space-y-2">
                  {Array.from({ length: blankCount }).map((_, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 shrink-0 w-24">Question {idx + 1}</span>
                      <select
                        value={getMultiAnswers()[idx] || "richtig"}
                        onChange={(e) => setMultiAnswer(idx, e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                      >
                        <option value="richtig">Richtig (True)</option>
                        <option value="falsch">Falsch (False)</option>
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <select
                  value={question.correctAnswer || "richtig"}
                  onChange={(e) => set("correctAnswer", e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                >
                  <option value="richtig">Richtig (True)</option>
                  <option value="falsch">Falsch (False)</option>
                </select>
              )}
            </div>
          )}

          {/* Fill in the blanks */}
          {isFillBlanks && (() => {
            const answers = getMultiAnswers();
            return (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {blankCount > 1 ? `Correct Answers (${blankCount} blanks)` : "Correct Answer"}
                </label>
                {Array.from({ length: blankCount }).map((_, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 shrink-0 w-20">
                      {blankCount > 1 ? `Q${(question.questionNumber ?? 1) + idx}` : "Answer"}
                    </span>
                    <input
                      type="text"
                      value={answers[idx] || ""}
                      onChange={(e) => setMultiAnswer(idx, e.target.value)}
                      placeholder={blankCount > 1 ? `Answer for Q${(question.questionNumber ?? 1) + idx}` : "Correct answer"}
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                    />
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Writing notice */}
          {isWriting && !isSprechen && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Writing questions require manual grading — no correct answer needed.
            </div>
          )}

          {/* Matching */}
          {isMatching && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer (JSON)</label>
              <textarea
                rows={2}
                value={question.correctAnswer || ""}
                onChange={(e) => set("correctAnswer", e.target.value)}
                placeholder='{"a": "1", "b": "2"}'
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>
          )}

          {/* ── Photo attachment ── */}
          <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Attachments
              </p>

              {/* Photo */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-700 font-medium mb-2">
                  <FaImage className="text-[#009fe3]" size={13} />
                  Question Photo
                  <span className="text-xs text-gray-400 font-normal">— one image can cover multiple questions</span>
                </div>
                {question.contextImageUrl ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <img
                        src={question.contextImageUrl}
                        alt="Question"
                        className="h-20 w-auto rounded border border-gray-200 object-contain"
                      />
                      <button
                        onClick={() => imgRef.current?.click()}
                        className="text-xs text-[#009fe3] hover:underline"
                      >
                        Replace
                      </button>
                    </div>
                    {/* Photo question count prompt */}
                    {supportsMultiAnswer && (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <FaImage size={12} className="text-amber-600 shrink-0" />
                        <span className="text-sm text-amber-800 shrink-0">How many questions in this photo?</span>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={blankCountStr}
                          onChange={(e) => {
                            setBlankCountStr(e.target.value);
                            const n = parseInt(e.target.value, 10);
                            if (!isNaN(n) && n >= 1 && n <= 20) updateBlankCount(n);
                          }}
                          onBlur={() => {
                            const n = parseInt(blankCountStr, 10);
                            const clamped = Math.max(1, Math.min(20, isNaN(n) ? 1 : n));
                            setBlankCountStr(String(clamped));
                            updateBlankCount(clamped);
                          }}
                          className="w-16 px-2 py-1 border border-amber-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        <span className="text-xs text-amber-600">
                          → {blankCount} answer field{blankCount !== 1 ? "s" : ""} will be shown
                        </span>
                      </div>
                    )}
                  </div>
                ) : pendingImg ? (
                  <div className="flex items-center gap-3">
                    <FaImage className="text-[#009fe3] shrink-0" size={14} />
                    <span className="text-sm text-gray-700 truncate max-w-xs">{pendingImg.name}</span>
                    <button
                      onClick={() => { uploadFile(pendingImg, "image"); setPendingImg(null); }}
                      disabled={uploadingImg}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#009fe3] text-white rounded-lg text-sm hover:bg-[#007cb5] disabled:opacity-50 shrink-0"
                    >
                      {uploadingImg ? <FaSpinner className="animate-spin" size={11} /> : null}
                      Upload
                    </button>
                    <button onClick={() => setPendingImg(null)} className="text-xs text-gray-400 hover:text-gray-600">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => imgRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#009fe3] hover:text-[#009fe3] transition-colors"
                  >
                    <FaImage size={12} />
                    {question.contextImageUrl ? "Replace photo" : "Attach photo"}
                  </button>
                )}
                <input
                  ref={imgRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setPendingImg(f);
                    e.target.value = "";
                  }}
                />
              </div>

            </div>

          {saveMsg && (
            <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <FaCheckCircle size={13} /> {saveMsg}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Done
          </button>
          <button
            onClick={handleSaveAndContinue}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] transition-colors disabled:opacity-50"
          >
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
            Save Question
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminClassTestEditor;
